import { cloneElement, type ComponentProps } from 'react';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import {
  IconElement,
  iconsApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import LanguageIcon from '@material-ui/icons/Language';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import SchoolIcon from '@material-ui/icons/School';
import BubbleChartIcon from '@material-ui/icons/BubbleChart';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import GavelIcon from '@material-ui/icons/Gavel';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem
          icon={iconComponent(item.icon)}
          to={item.href}
          text={item.title}
        />
      ));

      // Skipped items
      nav.take('page:search'); // Using search modal instead
      nav.take('page:notifications'); // Using NotificationsSidebarItem manually instead

      // Taken once and threaded into both groups below. `take` removes the
      // page from the list, so calling it a second time returns undefined.
      const catalog = navItems.take('page:catalog');

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {nav.take('page:home')}
            <CatalogNavGroup item={catalog} />
            {/* Titles stay owned by the plugins, so these read "Create" and
                "Register Existing Component". Each take must run before
                nav.rest() below: rest memoizes the elements it built, and a
                later take drops the page from the raw list without removing
                what was already rendered, leaving it in the scrolling list as
                well as in its group. Calling them inline keeps that order. */}
            <NavGroup
              links={[
                navItems.take('page:scaffolder'),
                navItems.take('page:catalog-import'),
              ]}
            />
            <NavGroup
              title="AI"
              links={[
                navItems.take('page:litellm'),
                'divider',
                ...catalogAiLinks(catalog),
              ]}
            />
            {/* Hand-written rather than taken, because neither page can be
                taken: a page only reaches `navItems` if its extension emits a
                route ref, and PageBlueprint emits one only when the plugin
                passes `routeRef` in its params. The tech-insights pages pass
                a path, a title and an icon but no route ref, so they are
                reachable by URL and invisible to the nav. That also means the
                paths below are the plugins' defaults, not resolved routes -
                overriding either page's `path` in app-config would need the
                same edit here. */}
            <NavGroup
              title="Insights"
              links={[
                {
                  title: 'Tech Insights',
                  href: '/tech-insights',
                  icon: <EmojiObjectsIcon />,
                },
                {
                  title: 'Maturity',
                  href: '/maturity',
                  icon: <EmojiEventsIcon />,
                },
              ]}
            />
            <SidebarDivider />
            <SidebarScrollWrapper>
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <NotificationsSidebarItem />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            {/* RBAC contributes a nav item of its own - its page passes a route
                ref - so taking it here is what keeps it out of the scrolling
                list above and next to the other administrative pages. */}
            {nav.take('page:rbac')}
            {nav.take('page:app-visualizer')}
            {nav.take('page:user-settings')}
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});

/**
 * A page as contributed by its plugin, or a hand-written link into one. The
 * shape `NavContentNavItem` already has, so taken items pass straight through.
 */
type NavLink = {
  icon?: IconElement;
  href: string;
  title: string;
};

/**
 * An icon element as the component prop the sidebar wants, forwarding the props
 * it passes down. `SidebarSubmenuItem` sizes its icon with `fontSize="small"`
 * and nothing else, so a wrapper that swallows the prop renders it a size too
 * big for the row.
 */
function iconComponent(
  icon?: IconElement,
): ComponentProps<typeof SidebarItem>['icon'] {
  return props => (icon ? cloneElement(icon, props) : null);
}

/** A submenu row, or a rule between two runs of them. */
type NavLinks = (NavLink | 'divider' | undefined)[];

/**
 * One sidebar entry standing for a group of pages, with the rest of the group
 * in a submenu behind it. Clicking the entry lands on `item`, or on the first
 * link when there is no separate landing page.
 *
 * Links whose page no plugin contributed arrive as `undefined` and drop out, so
 * a group is written the same way whether or not every page it names is
 * installed. One that ends up with a single link renders as a plain item rather
 * than a submenu repeating it.
 */
function NavGroup(props: { title?: string; item?: NavLink; links?: NavLinks }) {
  const links = props.links ?? [];
  const rows = links.filter(
    (link): link is NavLink => !!link && link !== 'divider',
  );
  const anchor = props.item ?? rows[0];

  if (!anchor) {
    return null;
  }

  const title = props.title ?? anchor.title;

  if (rows.length < 2) {
    return (
      <SidebarItem
        icon={iconComponent(anchor.icon)}
        to={anchor.href}
        text={title}
      />
    );
  }

  return (
    <SidebarItem
      icon={iconComponent(anchor.icon)}
      to={anchor.href}
      text={title}
    >
      <SidebarSubmenu title={title}>
        {links.map((link, index) => {
          if (!link) {
            return null;
          }
          if (link === 'divider') {
            return <SidebarDivider key={`divider-${index}`} />;
          }
          return (
            <SidebarSubmenuItem
              key={link.href}
              title={link.title}
              to={link.href}
              icon={iconComponent(link.icon)}
            />
          );
        })}
      </SidebarSubmenu>
    </SidebarItem>
  );
}

/**
 * The catalog, with a submenu of links into it filtered down to a single kind
 * or component type. The route, title and icon stay owned by the plugin — they
 * arrive as the nav item it contributes.
 */
function CatalogNavGroup({ item }: { item?: NavLink }) {
  const icons = useApi(iconsApiRef);

  if (!item) {
    return null;
  }

  const filter = (title: string, query: string, icon?: IconElement) => ({
    title,
    href: `${item.href}?${query}`,
    icon,
  });
  // A kind with no registered icon resolves to undefined, so kinds outside the
  // builtin set pass their own.
  const kind = (title: string, k: string, fallback?: IconElement) =>
    filter(title, `filters[kind]=${k}`, icons.icon(`kind:${k}`) ?? fallback);

  return (
    <NavGroup
      item={item}
      links={[
        kind('Domains', 'domain'),
        kind('Systems', 'system'),
        kind('Components', 'component'),
        filter(
          'Libraries',
          'filters[kind]=component&filters[type]=library',
          <LibraryBooksIcon />,
        ),
        filter(
          'Websites',
          'filters[kind]=component&filters[type]=website',
          <LanguageIcon />,
        ),
        kind('APIs', 'api'),
        kind('AI Resources', 'airesource', <BubbleChartIcon />),
        'divider',
        kind('Resources', 'resource'),
        'divider',
        kind('Groups', 'group'),
        kind('Users', 'user'),
      ]}
    />
  );
}

/**
 * Links into the catalog, filtered down to what `catalog/ai` declares.
 *
 * The rows straddle two kinds: skills and rules are `AiResource` narrowed by
 * `spec.type`, while an MCP server is an `API`. No single kind filter reaches
 * both, so each gets its own row.
 *
 * Rules links to an empty list for now — nothing under `catalog/ai` declares a
 * `type: rule` resource yet. The row is here so it fills in on its own once
 * something does.
 *
 * The catalog's own href is threaded in rather than written out, so these
 * follow the plugin if its route moves. If the catalog is not installed there
 * is nothing to link into and the rows drop out.
 */
function catalogAiLinks(catalog?: NavLink): NavLinks {
  if (!catalog) {
    return [];
  }

  return [
    {
      title: 'Skills',
      href: `${catalog.href}?filters[kind]=airesource&filters[type]=skill`,
      icon: <SchoolIcon />,
    },
    {
      title: 'Rules',
      href: `${catalog.href}?filters[kind]=airesource&filters[type]=rule`,
      icon: <GavelIcon />,
    },
    {
      title: 'MCP Servers',
      href: `${catalog.href}?filters[kind]=api&filters[type]=mcp-server`,
      icon: <DeviceHubIcon />,
    },
  ];
}
